import { createBimSceneFileSignedDownloadUrl } from "apis/procore/bimSceneFileSignedDownloadUrl";
import { log } from "utils/log";

import type { DrawingData } from "../types";
import { readMinimapFileOffline } from "./offlineStore";
import {
    buildTileSetFileUrl,
    isDevManifestOverride,
    type ParsedManifestUrl,
    resolveTiledManifestUrl,
    TILE_MANIFEST_RELATIVE_PATH,
} from "./tiledSource";

const l = log.miniMap.tiled;

/**
 * Inputs for the tiled route of one drawing (RFD00143 option C). Presence
 * means this drawing goes through tiles; `undefined` means the PDF route.
 */
export type TilingRequest = {
    companyId: number;
    projectId: number;
    sceneId: string;
    /** Scene version the sheets were resolved from (`current` when unknown). */
    sceneVersionId: string;
    /** Scene-file placement id (`platform_file_instance_id` from plans/extended). */
    platformFileInstanceId?: string;
};

/**
 * Bundle the tiled-route inputs for one drawing, or `undefined` to leave it
 * on the PDF route. This is the only place the route is decided.
 *
 * **The host's rollout flag is absolute.** `enabled: false` returns
 * `undefined` for every drawing on every platform, before anything else is
 * considered — no `platform_file_status` read, no signed-download-URL call,
 * no manifest fetch, no dev override. Turning the LaunchDarkly flag off
 * therefore restores the legacy PDF behavior byte for byte, depending on
 * nothing the tiled route introduced.
 *
 * **Web gets `undefined` even with the flag on** (BM-13878). Web has neither
 * the memory pressure nor the pdf.js Android-compatibility problem the tiled
 * route exists to solve, and the route's cost — a mini map that only appears
 * once Model Manager finishes importing, minutes after mapping — is one web
 * shouldn't pay. The sole exception is the dev manifest override, which is
 * how tiling is exercised on a desktop browser and is compiled out of
 * production.
 *
 * Gating here rather than inside `selectMinimapTilingEnabled` keeps the host
 * flag meaning "the host enabled tiling" rather than "tiling is active on
 * this platform". The offline subsystem reads that flag too, and
 * RFD00000145 §5 deliberately scopes its incremental caching to any Procore
 * design rather than mobile alone, so that it needs no changes if tiling
 * ever reaches web.
 */
export function buildTilingRequest(
    enabled: boolean,
    procoreContext: { companyId: number; projectId: number; sceneVersionId?: string } | null | undefined,
    sceneId: string | null | undefined,
    drawingData: Pick<DrawingData, "url" | "platformFileInstanceId">,
    isMobileDesign: boolean,
): TilingRequest | undefined {
    if (!enabled || !procoreContext || !sceneId) {
        return undefined;
    }
    if (!isMobileDesign && !isDevManifestOverride(drawingData.url)) {
        return undefined;
    }
    return {
        companyId: procoreContext.companyId,
        projectId: procoreContext.projectId,
        sceneId,
        sceneVersionId: procoreContext.sceneVersionId ?? "current",
        platformFileInstanceId: drawingData.platformFileInstanceId,
    };
}

/**
 * Mint (or re-mint) a live tile set root from Platform's signed-download-URL
 * endpoint. `null` when the endpoint is unreachable or returns nothing
 * usable (e.g. no import yet).
 *
 * Deduped and cached by `createBimSceneFileSignedDownloadUrl` itself, keyed
 * by placement id and reused until near expiry -- so it costs one network
 * round trip the first time it's called for a given signature window,
 * whether that call happens here (no cached manifest yet) or later, lazily,
 * from `fetchTileSetFile` on a tile's first OPFS miss. There is no reason to
 * pay it eagerly just because a manifest happened to already be cached.
 */
function resolveLiveBaseUrl(request: TilingRequest): Promise<string | null> {
    return createBimSceneFileSignedDownloadUrl({
        companyId: request.companyId,
        projectId: request.projectId,
        sceneId: request.sceneId,
        sceneVersionId: request.sceneVersionId,
        fileInstanceId: request.platformFileInstanceId!,
    });
}

/**
 * Locate a drawing's tile set manifest, or `null` when it has none (→ PDF
 * route). Tries the dev direct-manifest URL / localStorage override first.
 *
 * Then, whenever this drawing has a `platformFileInstanceId`, probes OPFS for
 * an already-cached manifest (RFD00000145): a tile set's identity is treated
 * as immutable once resolved (see `minimapFilePath`'s doc), so a cached
 * manifest doesn't need to be re-proven against the network before use. A
 * hit resolves an offline-only ref immediately -- no network call of any
 * kind, not even in the background -- but still carries a `refreshBaseUrl`
 * resolver, so `fetchTileSetFile` can mint a live prefix lazily, only if and
 * when a tile actually misses OPFS (a pan or zoom into something this
 * device hasn't cached yet). A failure there just fails that one tile
 * fetch; it doesn't affect the manifest, which is already in hand.
 *
 * A cache miss falls through to the RFD00143 option C flow (scene-file
 * placement id → Platform signed-download-URL endpoint →
 * `tile/manifest_1.json` under the returned prefix), blocking this time:
 * with nothing cached, availability must be proven by actually reaching the
 * endpoint. Any failure -- offline, or the endpoint unreachable/empty --
 * returns `null`, same as PDF in `downloadDrawingData`.
 */
export async function resolveTiledManifest(
    drawingUrl: string,
    request: TilingRequest,
): Promise<ParsedManifestUrl | null> {
    const direct = resolveTiledManifestUrl(drawingUrl);
    if (direct) {
        return direct;
    }

    if (!request.platformFileInstanceId) {
        return null;
    }

    const cachedManifest = await readMinimapFileOffline(request.platformFileInstanceId, TILE_MANIFEST_RELATIVE_PATH);
    if (cachedManifest) {
        l.debug("Resolved tile set from OPFS:", drawingUrl);
        return {
            platformFileInstanceId: request.platformFileInstanceId,
            refreshBaseUrl: () => resolveLiveBaseUrl(request),
        };
    }

    if (navigator.onLine) {
        const baseUrl = await resolveLiveBaseUrl(request);
        if (baseUrl) {
            const manifestUrl = buildTileSetFileUrl(baseUrl, TILE_MANIFEST_RELATIVE_PATH);
            l.debug("Resolved tile set via signed download URL:", drawingUrl, manifestUrl);
            return {
                baseUrl,
                manifestUrl,
                platformFileInstanceId: request.platformFileInstanceId,
                refreshBaseUrl: () => resolveLiveBaseUrl(request),
            };
        }
    }

    return null;
}
